import React from 'react'
import { createRoot } from 'react-dom/client'
import { App } from './App'
import { TracePanel } from './components/TracePanel'
import { TraceProvider } from './lib/trace'
import { font, theme } from './theme'

// A small reset, inline rather than a stylesheet, so the app is one bundle.
const style = document.createElement('style')
style.textContent = `
  @font-face {
    font-family: 'Nunito'; font-style: normal; font-weight: 200 1000;
    font-display: swap; src: url('/fonts/nunito-latin.woff2') format('woff2');
  }
  @font-face {
    font-family: 'Fredoka'; font-style: normal; font-weight: 300 700;
    font-display: swap; src: url('/fonts/fredoka-latin.woff2') format('woff2');
  }
  :root { color-scheme: only light; }
  h1, h2, h3 { font-family: ${font.display}; font-weight: 600; letter-spacing: 0.005em; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body {
    font-family: ${font.body};
    background: ${theme.bg};
    color: ${theme.text};
    -webkit-text-size-adjust: 100%;
    -webkit-tap-highlight-color: transparent;
  }
  button { font-family: inherit; }
  input, select, textarea { font-family: inherit; }
  /* Nothing should scroll sideways, on any screen. 'clip', not 'hidden':
     hidden turns the page into its own scroll box, and then nothing can
     stay pinned to the top while you scroll (the front page's top bar). */
  html, body { overflow-x: clip; }
`
document.head.appendChild(style)

// A newer website, picked up without anyone pressing reload (13 Oct 2026).
// A full page load always gets the newest version, but a tablet that keeps
// the app open would run the old one for days. So when the app comes back
// into view, and every 15 minutes while it is open, it compares its build
// with /version.json and reloads if a newer one is out. Never during a
// practice session (the Session screen sets the flag); the next check after
// the session ends does it.
declare const __BUILD_ID__: string
if (import.meta.env.PROD) {
  const check = () => {
    if (document.visibilityState !== 'visible' || (window as { __wsInSession?: boolean }).__wsInSession) return
    fetch('/version.json', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((v: { build?: string } | null) => { if (v?.build && v.build !== __BUILD_ID__) location.reload() })
      .catch(() => { /* offline: try again later */ })
  }
  document.addEventListener('visibilitychange', check)
  window.setInterval(check, 15 * 60 * 1000)
}

// The app kept on the device, so it opens on a bad connection (public/sw.js).
// Built copies only: in development it would serve stale files while editing.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => { /* the app works without it */ })
  })
}

const el = document.getElementById('root')
if (!el) throw new Error('no #root in index.html')

createRoot(el).render(
  <React.StrictMode>
    {/* The provenance overlay wraps everything so any screen can label a
        value, and renders nothing at all until it is switched on. */}
    <TraceProvider>
      <App />
      <TracePanel />
    </TraceProvider>
  </React.StrictMode>,
)
